// ─────────────────────────────────────────────────────────────────────────────
// Client-side mock backend for the Latency Test demo.
//
// The original BG-Demo ran an Express + SQLite server for auth, balances, bets,
// casino and payments. This demo is a static Cloudflare Pages app, so all of
// that "operator backend" is mocked in the browser (localStorage-backed). What
// is NOT mocked is the GeoComply integration — the real BrowserGuard SDK, the
// real license, and the real geolocation decrypt all run via Pages Functions
// under /api/geocomply/*. The operator (LatencyTestBR1 / LatencyTestLatam) is
// chosen on the login screen and threaded through every GeoComply call.
//
// Sports data is served live by the /api/games/<league> Pages Function (ESPN
// proxy), with a static fallback so the grid is never empty.
// ─────────────────────────────────────────────────────────────────────────────

const SESSION_KEY = 'latency_demo_session';
const STARTING_BALANCE = 1000.0;
const DEMO_USERS = ['bg-demo-1', 'bg-demo-2', 'bg-demo-3', 'bg-demo-4', 'bg-demo-5'];

const wait = (ms = 250) => new Promise((r) => setTimeout(r, ms));
const balKey = (u) => `latency_demo_balance_${u}`;
const txKey = (u) => `latency_demo_txns_${u}`;

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); }
  catch { return null; }
}
function setSession(user) { localStorage.setItem(SESSION_KEY, JSON.stringify(user)); }
function clearSession() { localStorage.removeItem(SESSION_KEY); }

function readBalance(username) {
  const raw = localStorage.getItem(balKey(username));
  return raw == null ? null : parseFloat(raw);
}
function writeBalance(username, value) {
  localStorage.setItem(balKey(username), String(Math.max(0, Number(value.toFixed(2)))));
}
function ensureBalance(username, initial = STARTING_BALANCE) {
  let bal = readBalance(username);
  if (bal == null) { bal = initial; writeBalance(username, bal); }
  return bal;
}

function recordTxn(username, txn) {
  let list = [];
  try { list = JSON.parse(localStorage.getItem(txKey(username)) || '[]'); } catch {}
  const entry = { id: list.length + 1, timestamp: new Date().toISOString(), ...txn };
  list.unshift(entry);
  localStorage.setItem(txKey(username), JSON.stringify(list.slice(0, 200)));
  return entry;
}

function currentUsername() {
  const s = getSession();
  return s?.username || DEMO_USERS[0];
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = async (username, password, browserGuardToken, operator) => {
  await wait();
  if (!username) {
    return { success: false, message: 'Username is required' };
  }
  const balance = ensureBalance(username);
  const user = {
    id: username,
    username,
    email: `${username}@example.com`,
    balance,
    isVerified: true,
    // GeoComply operator chosen at login — persisted so a page refresh restores it.
    operator: operator || null,
  };
  setSession(user);
  return { success: true, message: 'Login successful', user, geoData: null };
};

export const logout = async () => {
  await wait(100);
  clearSession();
  return { success: true, message: 'Logout successful' };
};

export const checkSession = async () => {
  await wait(100);
  const user = getSession();
  if (user) {
    return { authenticated: true, user: { ...user, balance: readBalance(user.username) ?? STARTING_BALANCE }, geoData: null };
  }
  return { authenticated: false };
};

export const register = async (username, email /*, password */) => {
  await wait();
  if (!username || !email) return { success: false, message: 'All fields are required' };
  // New accounts start at $0 (deposit to play) — matches the original behaviour.
  if (readBalance(username) == null) writeBalance(username, 0);
  return { success: true, message: 'Registration successful', emailSent: false, emailPreviewUrl: null };
};

// ── Balance / payments ──────────────────────────────────────────────────────
export const getUserBalance = async () => {
  await wait(80);
  const u = currentUsername();
  return { success: true, balance: ensureBalance(u) };
};

export const getPaymentLimits = async () => {
  await wait(60);
  return {
    success: true,
    deposit: { min: 10, max: 10000 },
    withdrawal: { min: 20, max: 10000 },
  };
};

export const createDepositIntent = async (amount) => {
  await wait();
  const u = currentUsername();
  const amt = parseFloat(amount);
  if (!amt || amt <= 0) return { success: false, message: 'Invalid amount' };
  writeBalance(u, ensureBalance(u) + amt);
  recordTxn(u, { type: 'deposit', status: 'completed', amount: amt, description: `Deposit $${amt.toFixed(2)}` });
  return { success: true, clientSecret: `mock_deposit_${Date.now()}` };
};

export const createWithdrawal = async (amount) => {
  await wait();
  const u = currentUsername();
  const amt = parseFloat(amount);
  const bal = ensureBalance(u);
  if (!amt || amt <= 0) return { success: false, message: 'Invalid amount' };
  if (amt > bal) return { success: false, message: 'Insufficient balance' };
  writeBalance(u, bal - amt);
  recordTxn(u, { type: 'withdrawal', status: 'completed', amount: -amt, description: `Withdrawal $${amt.toFixed(2)}` });
  return { success: true, message: 'Withdrawal initiated', newBalance: readBalance(u) };
};

export const getTransactions = async (limit = 50, offset = 0, type = null) => {
  await wait(80);
  const u = currentUsername();
  let list = [];
  try { list = JSON.parse(localStorage.getItem(txKey(u)) || '[]'); } catch {}
  if (type) list = list.filter((t) => t.type === type);
  return { success: true, transactions: list.slice(offset, offset + limit), total: list.length };
};

// ── Sportsbook bets ───────────────────────────────────────────────────────────
export const placeBets = async (bets) => {
  await wait();
  const u = currentUsername();
  const total = bets.reduce((sum, b) => sum + (parseFloat(b.stake) || 0), 0);
  const bal = ensureBalance(u);
  if (total <= 0) return { success: false, message: 'Enter a stake for your bets' };
  if (total > bal) return { success: false, message: 'Insufficient balance' };
  writeBalance(u, bal - total);
  const results = bets.map((b) => {
    const txn = recordTxn(u, {
      type: 'bet_placed', status: 'completed', amount: -(parseFloat(b.stake) || 0),
      description: `Bet: ${b.team} (${b.game})`,
    });
    return { status: 'placed', betId: txn.id, transactionId: `BET-${txn.id}-${Date.now().toString().slice(-5)}` };
  });
  return { success: true, results, newBalance: readBalance(u) };
};

// ── Games (live via Pages Function, static fallback) ──────────────────────────
async function fetchGames(league) {
  try {
    const res = await fetch(`/api/games/${league}`);
    if (res.ok) {
      const data = await res.json();
      if (data?.success && Array.isArray(data.games) && data.games.length) return data;
    }
  } catch (e) { /* fall through to static */ }
  return { success: true, games: fallbackGames(league), source: 'fallback' };
}

export const getFeaturedGames = () => fetchGames('featured');
export const getSoccerGames = () => fetchGames('soccer');
export const getNBAGames = () => fetchGames('nba');
export const getNFLGames = () => fetchGames('nfl');
export const getMLBGames = () => fetchGames('mlb');
export const getNHLGames = () => fetchGames('nhl');

// Static fallback so the grid is never empty (e.g. local dev without functions,
// or ESPN unreachable). Dates are stamped to "today" at call time.
function fallbackGames(league) {
  const today = new Date();
  const iso = (h, m = 0) => { const d = new Date(today); d.setHours(h, m, 0, 0); return d.toISOString(); };
  const t = (h, m = 0) => new Date(iso(h, m)).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const sets = {
    soccer: [
      { league: 'World Cup', home: 'Canada', away: 'Mexico', homeOdds: 180, awayOdds: 150 },
      { league: 'World Cup', home: 'USA', away: 'England', homeOdds: 165, awayOdds: 160 },
      { league: 'World Cup', home: 'Brazil', away: 'Argentina', homeOdds: 135, awayOdds: 195 },
      { league: 'World Cup', home: 'France', away: 'Germany', homeOdds: 150, awayOdds: 175 },
      { league: 'World Cup', home: 'Spain', away: 'Portugal', homeOdds: 140, awayOdds: 185 },
    ],
    nba: [
      { league: 'NBA', home: 'Toronto Raptors', away: 'Boston Celtics', homeOdds: 145, awayOdds: -170 },
      { league: 'NBA', home: 'Denver Nuggets', away: 'LA Lakers', homeOdds: -125, awayOdds: 105 },
      { league: 'NBA', home: 'Golden State Warriors', away: 'Phoenix Suns', homeOdds: -110, awayOdds: -110 },
    ],
    nfl: [
      { league: 'NFL', home: 'Buffalo Bills', away: 'Miami Dolphins', homeOdds: -180, awayOdds: 155 },
      { league: 'NFL', home: 'Kansas City Chiefs', away: 'Denver Broncos', homeOdds: -240, awayOdds: 200 },
    ],
    mlb: [
      { league: 'MLB', home: 'Toronto Blue Jays', away: 'New York Yankees', homeOdds: 120, awayOdds: -140 },
      { league: 'MLB', home: 'LA Dodgers', away: 'SF Giants', homeOdds: -150, awayOdds: 130 },
    ],
    nhl: [
      { league: 'NHL', home: 'Edmonton Oilers', away: 'Calgary Flames', homeOdds: -135, awayOdds: 115 },
      { league: 'NHL', home: 'Toronto Maple Leafs', away: 'Montreal Canadiens', homeOdds: -160, awayOdds: 140 },
    ],
  };
  const leagueKey = league === 'featured' ? null : league;
  const picked = leagueKey ? (sets[leagueKey] || []) : [...sets.soccer, ...sets.nba, ...sets.nhl, ...sets.nfl, ...sets.mlb];
  return picked.map((g, i) => ({
    id: `fb-${league}-${i}`,
    ...g,
    time: t(18 + (i % 4), (i * 15) % 60),
    date: iso(18 + (i % 4)),
    isLive: false,
    isFinal: false,
    homeScore: 0,
    awayScore: 0,
    clock: '',
    homeLogo: null,
    awayLogo: null,
  }));
}

// ── Casino: client-side Blackjack ─────────────────────────────────────────────
const activeGames = {};
const SUITS = ['♠', '♥', '♦', '♣'];
const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function buildDeck() {
  const deck = [];
  for (const suit of SUITS) for (const value of VALUES) deck.push({ suit, value });
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}
function cardNumericValue(card) {
  if (['J', 'Q', 'K'].includes(card.value)) return 10;
  if (card.value === 'A') return 11;
  return parseInt(card.value, 10);
}
function handValue(hand) {
  let total = 0, aces = 0;
  for (const card of hand) {
    if (card.value === 'A') { aces++; total += 11; } else { total += cardNumericValue(card); }
  }
  while (total > 21 && aces > 0) { total -= 10; aces--; }
  return total;
}
function dealerVisibleValue(hand) { return handValue([hand[0]]); }
function resolveGame(game) {
  const p = handValue(game.playerHand), d = handValue(game.dealerHand);
  const pBJ = game.playerHand.length === 2 && p === 21;
  const dBJ = game.dealerHand.length === 2 && d === 21;
  if (p > 21) return { status: 'dealer_win', result: 'Bust! Dealer wins.', payout: 0 };
  if (pBJ && !dBJ) return { status: 'blackjack', result: 'Blackjack! You win!', payout: game.wager * 2.5 };
  if (dBJ && !pBJ) return { status: 'dealer_win', result: 'Dealer Blackjack. Dealer wins.', payout: 0 };
  if (d > 21) return { status: 'player_win', result: 'Dealer busts! You win!', payout: game.wager * 2 };
  if (p > d) return { status: 'player_win', result: 'You win!', payout: game.wager * 2 };
  if (d > p) return { status: 'dealer_win', result: 'Dealer wins.', payout: 0 };
  return { status: 'push', result: 'Push — wager returned.', payout: game.wager };
}
function safeGameView(game, includeFullDealer = false) {
  return {
    gameId: game.gameId,
    playerHand: game.playerHand,
    dealerHand: game.dealerHand,
    playerValue: handValue(game.playerHand),
    dealerValue: handValue(game.dealerHand),
    dealerVisibleValue: includeFullDealer ? handValue(game.dealerHand) : dealerVisibleValue(game.dealerHand),
    status: game.status,
    result: game.result || null,
    payout: game.payout || 0,
    wager: game.wager,
  };
}

export const startBlackjackGame = async (wager) => {
  await wait(150);
  const u = currentUsername();
  const stake = parseFloat(wager);
  if (!stake || stake <= 0) return { success: false, message: 'Invalid wager amount' };
  const bal = ensureBalance(u);
  if (stake > bal) return { success: false, message: 'Insufficient balance' };
  writeBalance(u, bal - stake);
  recordTxn(u, { type: 'bet_placed', status: 'completed', amount: -stake, description: `Blackjack wager $${stake}` });

  const deck = buildDeck();
  const game = {
    gameId: `bj-${u}-${Date.now()}`,
    deck,
    playerHand: [deck.pop(), deck.pop()],
    dealerHand: [deck.pop(), deck.pop()],
    wager: stake,
    status: 'playing',
  };

  if (handValue(game.playerHand) === 21 || handValue(game.dealerHand) === 21) {
    Object.assign(game, resolveGame(game));
    if (game.payout > 0) {
      writeBalance(u, readBalance(u) + game.payout);
      recordTxn(u, { type: 'bet_won', status: 'completed', amount: game.payout, description: `Blackjack payout $${game.payout.toFixed(2)}` });
    }
    return { success: true, game: safeGameView(game, true), newBalance: readBalance(u) };
  }

  activeGames[game.gameId] = game;
  return { success: true, game: safeGameView(game, false), newBalance: readBalance(u) };
};

export const blackjackAction = async (gameId, action) => {
  await wait(120);
  const u = currentUsername();
  const game = activeGames[gameId];
  if (!game) return { success: false, message: 'Game not found' };
  if (game.status !== 'playing') return { success: false, message: 'Game is already over' };

  const finish = (full) => {
    if (game.payout > 0) {
      writeBalance(u, readBalance(u) + game.payout);
      recordTxn(u, { type: 'bet_won', status: 'completed', amount: game.payout, description: `Blackjack payout $${game.payout.toFixed(2)}` });
    }
    delete activeGames[gameId];
    return { success: true, game: safeGameView(game, full), newBalance: readBalance(u) };
  };

  if (action === 'hit') {
    game.playerHand.push(game.deck.pop());
    const p = handValue(game.playerHand);
    if (p > 21) { Object.assign(game, resolveGame(game)); return finish(true); }
    if (p === 21) return stand(game, finish);
    return { success: true, game: safeGameView(game, false), newBalance: readBalance(u) };
  }
  if (action === 'stand') return stand(game, finish);
  return { success: false, message: 'Invalid action' };
};

function stand(game, finish) {
  while (handValue(game.dealerHand) < 17) game.dealerHand.push(game.deck.pop());
  Object.assign(game, resolveGame(game));
  return finish(true);
}

const api = {
  login, logout, checkSession, register,
  getUserBalance, getPaymentLimits, createDepositIntent, createWithdrawal, getTransactions,
  placeBets, getFeaturedGames, getNBAGames, getNFLGames, getMLBGames, getNHLGames,
  startBlackjackGame, blackjackAction,
};
export default api;
